import {
  GatewayControleQuinquennal,
  GatewayTicketControleQuinquennal,
} from "@conformite/gateway";
import {
  FilterSearch,
  InfoCard,
  InputSearch,
  MaxWidthContent,
  SkeletonCard,
  Button,
} from "@endya-conformite/ui-kit";
import ChevronDown from "@endya-conformite/ui-kit/src/assets/icons/chevron-down.svg?react";
import ChevronUp from "@endya-conformite/ui-kit/src/assets/icons/chevron-up.svg?react";
import { filterSearchByKeys } from "@src/components/Filter/filter.helper";
import { useFilterPagination } from "@src/components/Filter/useFilterPagination";
import { MainContentPageTemplate } from "@src/components/MainPageTemplate/MainContentPageTemplate";
import { useEntreprises } from "@src/store/store.entreprise";
import { StoreEntryFetchStatus } from "@src/store/store";
import { groupBy, keyBy, map, partition } from "lodash";
import { PropsWithChildren, useCallback, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { ControlQuinquennalCard } from "./ControleQuinquennalCard";
import styles from "./ControleQuinquennalPage.module.scss";
import { useControlesQuinquennaux } from "./useControlesQuinquennaux";

export function Collapse({
  isOpen,
  title,
  onToggle,
  children,
}: PropsWithChildren & {
  isOpen: boolean;
  title: string;
  onToggle: () => void;
}) {
  return (
    <>
      <div className={styles.collapseTitle}>
        <h2>{title}</h2>

        <Button
          variant="text"
          icon={isOpen ? <ChevronUp /> : <ChevronDown />}
          onClick={onToggle}
        >
          {isOpen ? "Voir moins" : "Voir plus"}
        </Button>
      </div>
      {isOpen ? children : null}
    </>
  );
}

export function ControleQuinquennalPage() {
  const { controlesQuinquennaux } = useControlesQuinquennaux();
  const { entreprisesBySiren, status: entreprisesStatus } = useEntreprises();
  const [searchParams] = useSearchParams();
  const millesimeParam = searchParams.get("millesime");
  const filteredControlesQuinquennaux = useMemo(() => {
    if (!millesimeParam) return controlesQuinquennaux;
    const millesime = Number(millesimeParam);
    return controlesQuinquennaux.filter((cq) => cq.millesime === millesime);
  }, [controlesQuinquennaux, millesimeParam]);
  const [isOpenBySection, setIsOpenBySection] = useState({
    preCommission: true,
    enRemediation: true,
    enCours: true,
    enCoursVerification: true,
    historique: true,
    annule: true,
  });

  const entreprisesById = useMemo(
    () => keyBy(Object.values(entreprisesBySiren ?? {}), "hubspotId"),
    [entreprisesBySiren]
  );

  const controlesQuinquennauxWithEntreprise = useMemo(
    () =>
      filteredControlesQuinquennaux.map((controle) => ({
        ...controle,
        raisonSociale: entreprisesById[controle.idEntreprise]?.raisonSociale,
      })),
    [entreprisesById, filteredControlesQuinquennaux]
  );

  const { content, filtersSelected, setFilterSelected } = useFilterPagination({
    content: controlesQuinquennauxWithEntreprise,
    filters: [
      { name: "raisonSociale", filter: filterSearchByKeys(["raisonSociale"]) },
    ],
  });

  const [
    controlesEnPreCommission,
    controlesEnCoursAvecRemediation,
    controlesEnCoursSansRemediations,
    controlesQuinquennauxByStatus,
  ] = useMemo(() => {
    const [cqPreCommission, others] = partition(
      content,
      (controle) =>
        controle.pipelineStage ===
          GatewayTicketControleQuinquennal.TicketControleQuinquennalStatus
            .PRE_COMMISSION ||
        // Un contrôle radié reste affiché comme en pré-commission (sans accès),
        // sauf s'il était déjà terminé ou annulé
        (controle.pipelineStage ===
          GatewayTicketControleQuinquennal.TicketControleQuinquennalStatus
            .RADIE &&
          controle.status !==
            GatewayControleQuinquennal.ControleQuinquennalStatus.TERMINE &&
          controle.status !==
            GatewayControleQuinquennal.ControleQuinquennalStatus.ANNULE)
    );

    const cqByStatus = groupBy(others, "status");

    const [cqEnCoursAvecRemediation, cqEnCoursSansRemediations] = partition(
      cqByStatus[GatewayControleQuinquennal.ControleQuinquennalStatus.EN_COURS],
      (controle) => controle.hasRemediations
    );

    return [
      cqPreCommission,
      cqEnCoursAvecRemediation,
      cqEnCoursSansRemediations,
      cqByStatus,
    ];
  }, [content]);

  const handleToggleIsOpenBySection = useCallback(
    (section: keyof typeof isOpenBySection) => () =>
      setIsOpenBySection((prev) => ({ ...prev, [section]: !prev[section] })),
    []
  );

  return (
    <MainContentPageTemplate
      title={
        millesimeParam
          ? `Contrôle quinquennal ${millesimeParam}`
          : "Contrôle quinquennal"
      }
      contentSelection={
        <FilterSearch
          search={
            <InputSearch
              placeholder="Raison sociale, SIREN"
              name="search"
              value={filtersSelected?.raisonSociale[0] || ""}
              onChange={(nextValue) => {
                setFilterSelected("raisonSociale", [nextValue.target.value]);
              }}
            />
          }
        />
      }
    >
      <MaxWidthContent size="large">
        {filteredControlesQuinquennaux.length === 0 &&
          entreprisesStatus === StoreEntryFetchStatus.Loaded && (
            <div className={styles.controleQuinquennal}>
              <h2>Collecte en cours</h2>
              <div className={styles.list}>
                {new Array(2).fill(0).map((_, index) => (
                  // eslint-disable-next-line react/no-array-index-key
                  <SkeletonCard key={index} height={0} />
                ))}
              </div>
            </div>
          )}

        {controlesEnCoursSansRemediations.length > 0 && (
          <div className={styles.controleQuinquennal}>
            <Collapse
              isOpen={isOpenBySection.enCours}
              title="Collecte en cours"
              onToggle={handleToggleIsOpenBySection("enCours")}
            >
              <div className={styles.list}>
                {map(
                  controlesEnCoursSansRemediations,
                  (controleQuinquennal) => (
                    <ControlQuinquennalCard
                      key={controleQuinquennal.id}
                      controleQuinquennal={controleQuinquennal}
                      entreprise={
                        entreprisesById[controleQuinquennal.idEntreprise]
                      }
                    />
                  )
                )}
              </div>
            </Collapse>
          </div>
        )}
        {controlesEnCoursAvecRemediation.length > 0 && (
          <div className={styles.controleQuinquennal}>
            <Collapse
              isOpen={isOpenBySection.enRemediation}
              title="En remédiation"
              onToggle={handleToggleIsOpenBySection("enRemediation")}
            >
              <div className={styles.list}>
                {map(controlesEnCoursAvecRemediation, (controleQuinquennal) => (
                  <ControlQuinquennalCard
                    key={controleQuinquennal.id}
                    controleQuinquennal={controleQuinquennal}
                    entreprise={
                      entreprisesById[controleQuinquennal.idEntreprise]
                    }
                  />
                ))}
              </div>
            </Collapse>
          </div>
        )}
        {controlesQuinquennauxByStatus[
          GatewayControleQuinquennal.ControleQuinquennalStatus.DEPOSE
        ]?.length > 0 && (
          <div className={styles.controleQuinquennal}>
            <Collapse
              isOpen={isOpenBySection.enCoursVerification}
              title="Vérification en cours"
              onToggle={handleToggleIsOpenBySection("enCoursVerification")}
            >
              <div className={styles.list}>
                {map(
                  controlesQuinquennauxByStatus[
                    GatewayControleQuinquennal.ControleQuinquennalStatus.DEPOSE
                  ],
                  (controleQuinquennal) => (
                    <ControlQuinquennalCard
                      key={controleQuinquennal.id}
                      controleQuinquennal={controleQuinquennal}
                      entreprise={
                        entreprisesById[controleQuinquennal.idEntreprise]
                      }
                    />
                  )
                )}
              </div>
            </Collapse>
          </div>
        )}
        {controlesEnPreCommission.length > 0 && (
          <div className={styles.controleQuinquennal}>
            <Collapse
              isOpen={isOpenBySection.preCommission}
              title="Pré-commission"
              onToggle={handleToggleIsOpenBySection("preCommission")}
            >
              <InfoCard type="warning">
                Les contrôle quinquennaux ci-dessous sont à l&apos;état de
                pré-commission. Une lettre recommandée vous sera adressée
                concernant la commission disciplinaire.
              </InfoCard>
              <div className={styles.list}>
                {map(controlesEnPreCommission, (controleQuinquennal) => (
                  <ControlQuinquennalCard
                    key={controleQuinquennal.id}
                    controleQuinquennal={{
                      ...controleQuinquennal,
                      status:
                        GatewayControleQuinquennal.ControleQuinquennalStatus
                          .ANNULE, // On met le status en annulé afin de ne pas afficher le bouton pour y accéder
                    }}
                    entreprise={
                      entreprisesById[controleQuinquennal.idEntreprise]
                    }
                  />
                ))}
              </div>
            </Collapse>
          </div>
        )}
        {controlesQuinquennauxByStatus[
          GatewayControleQuinquennal.ControleQuinquennalStatus.TERMINE
        ]?.length > 0 && (
          <div className={styles.controleQuinquennal}>
            <Collapse
              isOpen={isOpenBySection.historique}
              title="Historique"
              onToggle={handleToggleIsOpenBySection("historique")}
            >
              {map(
                controlesQuinquennauxByStatus[
                  GatewayControleQuinquennal.ControleQuinquennalStatus.TERMINE
                ],
                (controleQuinquennal) => (
                  <div className={styles.list}>
                    <ControlQuinquennalCard
                      controleQuinquennal={controleQuinquennal}
                      entreprise={
                        entreprisesById[controleQuinquennal.idEntreprise]
                      }
                    />
                  </div>
                )
              )}
            </Collapse>
          </div>
        )}

        {controlesQuinquennauxByStatus[
          GatewayControleQuinquennal.ControleQuinquennalStatus.ANNULE
        ]?.length > 0 && (
          <div className={styles.controleQuinquennal}>
            <Collapse
              isOpen={isOpenBySection.annule}
              title="Contrôle annulé"
              onToggle={handleToggleIsOpenBySection("annule")}
            >
              {map(
                controlesQuinquennauxByStatus[
                  GatewayControleQuinquennal.ControleQuinquennalStatus.ANNULE
                ],
                (controleQuinquennal) => (
                  <div className={styles.list}>
                    <ControlQuinquennalCard
                      controleQuinquennal={controleQuinquennal}
                      entreprise={
                        entreprisesById[controleQuinquennal.idEntreprise]
                      }
                    />
                  </div>
                )
              )}
            </Collapse>
          </div>
        )}
      </MaxWidthContent>
    </MainContentPageTemplate>
  );
}
